import { useTranslation } from 'react-i18next';
import {
  Button,
  cn,
  Flex,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';
import { ABOVE_OUTPUT_MODAL_CLASSNAME } from '@/shared/constants/output-modal-layer';

interface IOutputModalCarouselProps {
  currentIndex: number;
  itemCount: number;
  onPrevious?: () => void;
  onNext?: () => void;
}

export function OutputModalCarousel({
  currentIndex,
  itemCount,
  onPrevious,
  onNext,
}: IOutputModalCarouselProps) {
  const { t } = useTranslation('outputs');
  const previousLabel = t('outputs.modal.previous');
  const nextLabel = t('outputs.modal.next');

  return (
    <Flex
      align="center"
      justify="between"
      position="fixed"
      /* `w-37 h-10` is a 148x40 pill, held fixed so the position label's digit
         count cannot resize it as you page through the outputs. */
      className={cn(
        'output-modal-carousel bottom-4 left-1/2 w-37 h-10 -translate-x-1/2',
        ABOVE_OUTPUT_MODAL_CLASSNAME,
      )}
      role="group"
      aria-label={t('outputs.modal.navigation')}
    >
      <Tooltip>
        <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
          <span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 disabled:opacity-40"
              onClick={onPrevious}
              disabled={!onPrevious}
              aria-label={previousLabel}
              data-output-modal-carousel-button="previous"
            >
              <Icon type="ChevronLeft" size="sm" />
            </Button>
          </span>
        </TooltipTrigger>
        {/* Lattice's TooltipContent ships z-50; the modal backdrop is z-100
            and would otherwise sit above it. */}
        <TooltipContent className={ABOVE_OUTPUT_MODAL_CLASSNAME}>
          {previousLabel}
        </TooltipContent>
      </Tooltip>
      <Text
        level="small"
        color="muted"
        align="center"
        className="output-modal-carousel-position min-w-0 flex-1 text-background whitespace-nowrap tabular-nums"
      >
        {t('outputs.modal.position', {
          current: currentIndex + 1,
          total: itemCount,
        })}
      </Text>
      <Tooltip>
        <TooltipTrigger asChild onFocus={event => event.preventDefault()}>
          <span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8 disabled:opacity-40"
              onClick={onNext}
              disabled={!onNext}
              aria-label={nextLabel}
              data-output-modal-carousel-button="next"
            >
              <Icon type="ChevronRight" size="sm" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent className={ABOVE_OUTPUT_MODAL_CLASSNAME}>
          {nextLabel}
        </TooltipContent>
      </Tooltip>
    </Flex>
  );
}
